// パスワード再設定（個人設定画面）。Cognito の ForgotPassword / ConfirmForgotPassword をブラウザから直接呼ぶ。
//
// どちらも未認証で呼べる公開 API で、ポータルの App Client はシークレットを持たない（SPA）。
// Managed Login の再設定画面は Cognito のエラーを「リクエストが無効です」に丸めるため使わない。
// SSO 判定ドメインの本人性への確認コードの送信は、共有プールの Custom Message トリガーが拒否する
// （画面で再設定を隠すのは表示上の都合で、拒否はサーバー側が担う）。

import { getConfig } from "./config";

export class CognitoError extends Error {
  constructor(public readonly type: string, message: string) {
    super(message);
  }
}

async function cognito(action: "ForgotPassword" | "ConfirmForgotPassword", body: Record<string, string>): Promise<void> {
  const c = getConfig();
  const region = c.userPoolId.split("_")[0];
  const res = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify({ ClientId: c.clientId, ...body }),
  });
  if (res.ok) return;
  const e = (await res.json().catch(() => ({}))) as { __type?: string; message?: string };
  throw new CognitoError(errorType(e.__type), e.message ?? "");
}

/** `__type` は "com.amazonaws...#CodeMismatchException" の形でも返る。末尾の型名だけを取る。 */
export const errorType = (t: string | undefined): string => (t ?? "").split("#").pop() ?? "";

/** 確認コードを email に送る。Username は email（共有プールは email を Username 属性にしている）。 */
export const sendResetCode = (email: string) => cognito("ForgotPassword", { Username: email });

/** 確認コードと新しいパスワードで再設定を確定する。 */
export const confirmReset = (email: string, code: string, password: string) =>
  cognito("ConfirmForgotPassword", { Username: email, ConfirmationCode: code, Password: password });

/** Cognito のエラーを利用者向けの文言にする。 */
export function resetMessageFor(e: unknown): string {
  const type = e instanceof CognitoError ? e.type : "";
  const table: Record<string, string> = {
    CodeMismatchException: "確認コードが正しくありません。",
    ExpiredCodeException: "確認コードの有効期限が切れています。コードを再送してください。",
    InvalidPasswordException: "パスワードは 8 文字以上で、英大文字・英小文字・数字を含めてください。",
    LimitExceededException: "試行回数の上限に達しました。しばらく待ってからやり直してください。",
    TooManyRequestsException: "試行回数の上限に達しました。しばらく待ってからやり直してください。",
    TooManyFailedAttemptsException: "試行回数の上限に達しました。しばらく待ってからやり直してください。",
    CodeDeliveryFailureException: "確認コードのメールを送れませんでした。しばらく待ってからやり直してください。",
    NotAuthorizedException: "このアカウントではパスワードを再設定できません。",
    // Custom Message トリガーの拒否（SSO 判定ドメイン、または判定できなかった）。
    UserLambdaValidationException: "このアカウントではパスワードを再設定できません。組織の SSO でログインしてください。",
  };
  return table[type] ?? "パスワードを再設定できませんでした。しばらく待ってからやり直してください。";
}
